import React, {useCallback, useEffect} from 'react'
import Lottie from 'lottie-react'
import styled from 'styled-components'
import {TranslationKey, useTranslation} from 'i18n'
import {AuthCredentialType, ProfileEditKycStatus} from 'types'
import {useDidUpdate, useTimer} from 'utils'
import {Button, Icon, Modal, Paragraph} from 'common/components'
import convertUnit from 'lib/unit'
import animationData from 'assets/lottie/activity_indicator.json'

const StyledModal = styled(Modal)`
  box-sizing: border-box;
  width: 100%;
  max-width: ${convertUnit(335)};
  padding: ${convertUnit(20)};
  display: flex;
  text-align: center;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`
const StyledLottie = styled(Lottie)`
  height: ${convertUnit(56)};
  width: ${convertUnit(56)};
  margin-bottom: ${convertUnit(25)};
`
const StyledTitle = styled(Paragraph)`
  margin-bottom: ${convertUnit(8)};
`
const StyledButtonContainer = styled.div`
  display: flex;
  flex-direction: row;
  width: fill-available;
  margin-top: ${convertUnit(20)};
  gap: ${convertUnit(20)};
`
const StyledButton = styled(Button)`
  width: 100%;
`

interface GiftShopEditProfileModalKYCStatusProps {
  visible: boolean
  status: ProfileEditKycStatus | undefined
  method: AuthCredentialType
  onPrimaryAction(): void
  onSecondaryAction(): void
  loading?: boolean
}

export default function GiftShopEditProfileModalKYCStatus({
  visible,
  status,
  method,
  onPrimaryAction,
  onSecondaryAction,
  loading,
}: GiftShopEditProfileModalKYCStatusProps) {
  const {translate} = useTranslation()
  const {countdown, reset} = useTimer({duration: 5, countOnStart: false})

  const handleRenderTitle = useCallback((): TranslationKey => {
    switch (status) {
      case 'verified':
        return 'global:success'
      case 'missmatch':
      case 'spoof':
      case 'undetected':
        return 'giftShop:editCredentialSelfieFailedTitle'
      default:
        return 'giftShop:editCredentialSelfieUploadingTitle'
    }
  }, [status])

  const handleRenderDesc = useCallback((): TranslationKey => {
    switch (status) {
      case 'verified':
        return 'giftShop:editCredentialVerifSuccess'
      case 'missmatch':
      case 'spoof':
      case 'undetected':
        return 'giftShop:editCredentialSelfieFailedDesc'
      default:
        return 'giftShop:editCredentialSelfieUploadingDesc'
    }
  }, [status])

  useEffect(() => {
    if (status === 'verified') {
      reset()
    }
  }, [reset, status])

  useDidUpdate(() => {
    if (status === 'verified' && countdown === 0) {
      onPrimaryAction()
    }
  }, [countdown])

  return (
    <StyledModal
      visible={visible}
      onBackdropPress={
        status
          ? status === 'waiting'
            ? undefined
            : status === 'verified'
            ? onPrimaryAction
            : onSecondaryAction
          : undefined
      }>
      {status ? (
        status === 'waiting' ? (
          <StyledLottie animationData={animationData} />
        ) : (
          <Icon
            type={status === 'verified' ? 'check' : 'close'}
            color={status === 'verified' ? 'success_5' : 'gray_3'}
            size={48}
          />
        )
      ) : null}
      <StyledTitle fontSize="xl" fontWeight="bold">
        {translate(handleRenderTitle(), {context: status})}
      </StyledTitle>
      <Paragraph
        fontSize="l"
        color={status && status === 'waiting' ? 'gray_5' : 'black'}>
        {translate(handleRenderDesc(), {
          context: status === 'verified' ? method : status,
        })}
      </Paragraph>
      {status && status === 'verified' && (
        <Paragraph>
          {translate('giftShop:editCredentialVerifSuccessCooldown', {
            time: countdown,
          })}
        </Paragraph>
      )}
      {status && status === 'waiting' && (
        <Paragraph fontSize="m" color="gray_3">
          {translate('giftShop:editCredentialSelfieUploadingSubDesc')}
        </Paragraph>
      )}
      {status && status !== 'waiting' && (
        <StyledButtonContainer>
          {status !== 'verified' ? (
            <StyledButton
              label={translate('global:close')}
              color="primary_5"
              backgroundColor="white_3"
              onClick={onSecondaryAction}
            />
          ) : null}
          <StyledButton
            label={translate(
              status === 'verified' ? 'global:next' : 'global:tryAgain',
            )}
            isLoading={loading}
            onClick={onPrimaryAction}
          />
        </StyledButtonContainer>
      )}
    </StyledModal>
  )
}
